import { useEffect } from "react";
import { Select } from "@/components/ui/Select";
import { useActiveCompanies } from "@/hooks/useActiveCompanies";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { setSelectedCompany, setSelectedStore } from "@/store/appSlice";
import { notify } from "@/core/toast/notify";
import { clearPosSessionStorage } from "@/utils/posSessionStorage";

interface CompanySwitcherProps {
  compact?: boolean;
  className?: string;
}

export function CompanySwitcher({
  compact = true,
  className,
}: CompanySwitcherProps) {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.app.selectedCompany);
  const { companies, loading } = useActiveCompanies();

  useEffect(() => {
    if (selected || loading || companies.length !== 1) return;
    dispatch(setSelectedCompany(companies[0]));
  }, [companies, dispatch, loading, selected]);

  useEffect(() => {
    if (!selected || loading || companies.length === 0) return;
    if (!companies.some((c) => c.id === selected.id)) {
      dispatch(setSelectedCompany(null));
    }
  }, [companies, dispatch, loading, selected]);

  const onChange = (value: string) => {
    const company = companies.find((c) => String(c.id) === value);
    if (!company || company.id === selected?.id) return;
    clearPosSessionStorage();
    dispatch(setSelectedStore(null));
    dispatch(setSelectedCompany(company));
    notify.success(`Working in ${company.name}`);
  };

  if (!loading && companies.length === 0) {
    return null;
  }

  if (loading && companies.length === 0) {
    return null;
  }

  return (
    <Select
      label={compact ? undefined : "Company"}
      className={className ?? "min-w-[9rem] max-w-[15rem]"}
      placeholder={loading ? "Loading companies…" : "Select company"}
      disabled={loading || companies.length === 0}
      value={selected ? String(selected.id) : ""}
      options={companies.map((c) => ({
        value: String(c.id),
        label: `${c.name} (${c.code})`,
      }))}
      onChange={onChange}
    />
  );
}
